import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Users, ChevronDown, ChevronUp, MoreHorizontal, ArrowLeftRight, PlayCircle, Plus, Minus, Sun, Edit2, Trash2, Sparkles, CheckCircle2 } from 'lucide-react';
import { UnitConversionModal } from './UnitConversionModal';
import { convertIngredients, convertMethodTemperatures } from '../utils/unitConversion';
import { StepByStepCooking } from './StepByStepCooking';
import BottomNav from './BottomNav';
import { useWakeLock } from '../hooks/useWakeLock';
import { RecipeTimeChips } from './RecipeTimeChips';



export function RecipeDetail({ recipe, onBack, activeTab, onTabChange, onServesChange, onEdit, onDelete, onAddToMealPlan, showAddToMealPlan = false, showEditDelete = false, successBannerMessage = '' }) {
  // Track baseline for scaling; UI uses `currentServings`
  const baseServingsRef = useRef(Number(recipe.baseServings ?? recipe.servings) || 1);
  const lastRecipeIdRef = useRef(recipe.id);
  // Initialize baseline only when the recipe changes, not when parent updates servings.

  // Helper to scale a quantity string like "2 cups", "1 1/2 tbsp", "to taste"
  const scaleQuantityString = (q, factor) => {
    if (!q || typeof q !== 'string') return q || '';
    const trimmed = q.trim();
    // Skip non-numeric quantities
    if (/^(to taste|pinch|dash|some)$/i.test(trimmed)) return trimmed;
    // Extract leading number or fraction(s)
    const match = trimmed.match(/^([0-9]+(?:\.[0-9]+)?(?:\s+[0-9]+\/[0-9]+)?)\s*(.*)$/);
    if (!match) return trimmed;
    const [, numPart, rest] = match;
    // Convert "1 1/2" or "1/2" to decimal
    const parts = numPart.split(' ');
    let total = 0;
    for (const p of parts) {
      if (p.includes('/')) {
        const [n, d] = p.split('/').map(Number);
        if (!isNaN(n) && !isNaN(d) && d !== 0) total += n / d;
      } else {
        const v = Number(p);
        if (!isNaN(v)) total += v;
      }
    }
    const scaled = total * factor;
    // Round to sensible precision and reconstruct
    const rounded = Math.round(scaled * 100) / 100;
    return `${rounded}${rest ? ' ' + rest : ''}`;
  };

  const [currentUnitSystem, setCurrentUnitSystem] = useState(recipe.unitSystem);

  // UI-controlled servings; keep in sync with local `servings` and incoming recipe
  const [currentServings, setCurrentServings] = useState(Number(recipe.servings) || 1);
  const [isOptimisticServes, setIsOptimisticServes] = useState(false);
  useEffect(() => {
    if (typeof recipe.servings !== 'undefined') {
      setCurrentServings(Number(recipe.servings) || 1);
    }
  }, [recipe.servings]);
  // Initialize baseline only when the recipe changes, not when parent updates servings.
  useEffect(() => {
    if (lastRecipeIdRef.current !== recipe.id) {
      lastRecipeIdRef.current = recipe.id;
      const base = Number(recipe.servings) || 1;
      setCurrentServings(base);
      baseServingsRef.current = Number(recipe.baseServings ?? base) || base;
      setIsOptimisticServes(false);
    }
  }, [recipe.id, recipe.servings, recipe.baseServings]);
  // When ingredients update from server, stop optimistic preview
  useEffect(() => {
    setIsOptimisticServes(false);
  }, [recipe.ingredients]);

  const scaledIngredients = useMemo(() => {
    const baseline = Array.isArray(recipe.baseIngredients) ? recipe.baseIngredients : null;
    const persistedServings = Number(recipe.servings) || 1;
    const uiServings = Number(currentServings) || persistedServings;
    const baselineServings = Number(recipe.baseServings) || persistedServings || 1;
    const alreadyScaled = recipe.ingredientsScaledForServes === true;

    // If backend already scaled ingredients for the persisted serves and UI matches, trust server values
    if (alreadyScaled && uiServings === persistedServings && !isOptimisticServes) {
      const originalList = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
      return currentUnitSystem === recipe.unitSystem
        ? originalList
        : convertIngredients(originalList, currentUnitSystem);
    }

    // If we have numeric baselines, always scale from them so prefs/serves changes apply
    if (baseline && baseline.length) {
      const multiplier = baselineServings > 0 ? (uiServings / baselineServings) : 1;
      const numerics = baseline.map(ing => {
        const qtyNum = typeof ing.quantity === 'number' ? ing.quantity : (Number(ing.quantity) || 0);
        const scaledNum = Math.round(qtyNum * multiplier * 100) / 100;
        const unit = ing.unit || '';
        const qtyStr = unit ? `${scaledNum} ${unit}` : `${scaledNum}`;
        return { name: ing.name || '', quantity: qtyStr, unit, category: ing.category };
      });
      return currentUnitSystem === recipe.unitSystem ? numerics : convertIngredients(numerics, currentUnitSystem);
    }

    // Fallback: scale string quantities heuristically
    const originalList = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
    const baseList = currentUnitSystem === recipe.unitSystem
      ? originalList
      : convertIngredients(originalList, currentUnitSystem);
    const multiplier = baselineServings > 0 ? (uiServings / baselineServings) : 1;
    const previewList = baseList.map((ing) => {
      const name = ing.name || '';
      const q = ing.quantity || '';
      return { ...ing, name, quantity: scaleQuantityString(String(q), multiplier) };
    });
    return previewList;
  }, [recipe.baseIngredients, recipe.ingredients, recipe.ingredientsScaledForServes, recipe.baseServings, recipe.unitSystem, currentServings, currentUnitSystem, recipe.servings, isOptimisticServes]);

  const [isNutritionExpanded, setIsNutritionExpanded] = useState(false);
  const [isUnitConversionModalOpen, setIsUnitConversionModalOpen] = useState(false);
  const [isStepByStepMode, setIsStepByStepMode] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const { isSupported, isActive, simulatedMode, requestWakeLock, releaseWakeLock } = useWakeLock();
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    
    const handleMediaChange = (e) => {
      setIsLargeScreen(e.matches);
    };
    
    // Set initial value
    setIsLargeScreen(mediaQuery.matches);
    
    // Listen for changes
    mediaQuery.addEventListener('change', handleMediaChange);
    
    // Cleanup
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  
  // Convert ingredients (handled in scaledIngredients) and method based on current unit system
  

  const displayedMethod = currentUnitSystem === recipe.unitSystem
    ? recipe.method
    : convertMethodTemperatures(recipe.method, currentUnitSystem);

  // Recipe Details: show ingredients as a flat list (no categories)

  const handleServesIncrement = () => {
    const newServings = currentServings + 1;
    setCurrentServings(newServings);
    setIsOptimisticServes(true);
    onServesChange?.(newServings);
  };

  const handleConvert = () => {
    setIsUnitConversionModalOpen(true);
  };

  const handleUnitChange = (unit) => {
    setCurrentUnitSystem(unit);
    console.log('Unit system changed to:', unit);
  };

  const handleStartStepByStep = () => {
    setIsStepByStepMode(true);
  };

  // Cook mode keeps the screen awake. Its state shows on the button (pressed) and in the green
  // status box below the header while it's on. No toasts.
  const toggleCookMode = async () => {
    if (isActive) await releaseWakeLock();
    else await requestWakeLock();
  };

  const handleServesDecrement = () => {
    if (currentServings > 1) {
      const newServings = currentServings - 1;
      setCurrentServings(newServings);
      setIsOptimisticServes(true);
      onServesChange?.(newServings);
    }
  };

  // If in step-by-step mode, show that component instead
  if (isStepByStepMode) {
    return (
      <StepByStepCooking
        recipe={recipe}
        method={displayedMethod}
        ingredients={scaledIngredients}
        onBack={() => setIsStepByStepMode(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7F5] flex justify-center">
      <div 
        className="w-full bg-[#F8F7F5] min-h-screen relative pb-24"
        style={{ 
          maxWidth: isLargeScreen ? '1200px' : '448px',
          transition: 'max-width 0.3s ease'
        }}
      >      
      {/* Header with Back Button */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div 
            className="py-4 flex items-center gap-2"
            style={{ 
              paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
              paddingRight: isLargeScreen ? '3rem' : '1.5rem'
            }}
          >
          <button
              onClick={onBack}
              className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
              aria-label="Back"
            >
            <ArrowLeft className="w-5 h-5 text-gray-900" />
          </button>
          <h1 
              className="flex-1"
              style={{ 
                fontSize: isLargeScreen ? '32px' : '20px',
                transition: 'font-size 0.3s ease'
              }}
            >
              Recipe Details
            </h1>
          {isSupported && (
            <button
              type="button"
              onClick={toggleCookMode}
              // On: the Convert button's colours (sage, dark icon, 7.5:1) plus a darker green outline,
              // so the state isn't shown by colour alone. aria-pressed tells screen readers.
              className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-colors ${
                isActive
                  ? 'bg-[#AFBB8B] text-[#1F2814] border-[#5F6B3F] hover:bg-[#9AA876]'
                  : 'bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200'
              }`}
              aria-label="Cook mode"
              aria-pressed={isActive}
              title={isActive ? 'Cook mode is on: your screen will stay awake' : 'Turn on cook mode to keep your screen awake'}
            >
              <Sun className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
          {showEditDelete && (onEdit || onDelete) && (
            <button
              onClick={() => setIsActionsMenuOpen(true)}
              className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
              aria-label="More actions"
            >
              <MoreHorizontal className="w-5 h-5 text-gray-900" />
            </button>
          )}
        </div>
      </header>

      {/* Cook mode status: always mounted so turning it on is announced; visible while it's on. */}
      <div role="status" aria-live="polite">
        {isActive && (
          <div
            className="mx-auto mt-3 rounded-xl border border-[#BFDCCB] bg-[#EAF6EE] px-3 py-2 text-sm text-[#1F5D38]"
            style={{
              marginLeft: isLargeScreen ? '3rem' : '1.5rem',
              marginRight: isLargeScreen ? '3rem' : '1.5rem',
            }}
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1F5D38]" aria-hidden="true" />
              <span>
                {simulatedMode
                  ? "Cook mode is on, but your browser won't keep the screen awake right now (for example, in battery saver mode)."
                  : 'Cook mode is on. Your screen will stay awake while you cook.'}
              </span>
            </div>
          </div>
        )}
      </div>

      {successBannerMessage && (
        <div
          className="mx-auto mt-3 rounded-xl border border-[#BFDCCB] bg-[#EAF6EE] px-3 py-2 text-sm text-[#1F5D38]"
          style={{
            marginLeft: isLargeScreen ? '3rem' : '1.5rem',
            marginRight: isLargeScreen ? '3rem' : '1.5rem',
          }}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1F5D38]" />
            <span>{successBannerMessage}</span>
          </div>
        </div>
      )}

      
        {/* Hero Image */}
        <div className={`w-full aspect-[16/9] overflow-hidden bg-gray-100 ${successBannerMessage || isActive ? 'mt-4' : ''}`}>
          <img
            src={recipe.image}
            alt={recipe.name}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div 
          className="py-6"
          style={{ 
            paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
            paddingRight: isLargeScreen ? '3rem' : '1.5rem'
          }}
        >
          {/* Recipe Name */}
          <h2 
            className="mb-4"
            style={{ 
              fontSize: isLargeScreen ? '32px' : '24px',
              transition: 'font-size 0.3s ease'
            }}
          >
            {recipe.name}
          </h2>

          {recipe.shared ? (
            <div className="inline-flex items-center gap-2 bg-[#F0F3EC] px-3 py-1.5 rounded-full mb-4">
              <Sparkles className="w-4 h-4 text-[#8B9A7A] stroke-[#8B9A7A]" />
              <span className="text-sm text-[#6B7A54] font-medium">Starter Recipe</span>
            </div>
          ) : null}

          {/* Time and Servings Info - Compact Version */}
          <div className="flex items-center justify-between mb-4 text-sm">
            <div className="flex items-center gap-4">
              <RecipeTimeChips recipe={recipe} itemClassName="flex items-center gap-2 text-[#757575]" />
            </div>
            
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#757575]" />
              <span>Serves </span>
              <div className="flex items-center gap-1 bg-white rounded-full px-2 py-1 shadow-sm">
                <button
                  onClick={handleServesDecrement}
                  className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                  disabled={currentServings <= 1}
                  aria-label="Decrease servings"
                >
                  <Minus className="w-3 h-3 text-gray-600" />
                </button>
                <span className="w-6 text-center font-medium text-gray-900">{currentServings}</span>
                <button
                  onClick={handleServesIncrement}
                  className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Increase servings"
                >
                  <Plus className="w-3 h-3 text-gray-600" />
                </button>
              </div>
            </div>
          </div>

          {/* Meal type on its own row, above the tags. The accent fill matches the selected chip
              in the recipe form so the two read as the same piece of information. #2A1A16 rather
              than var(--accent-foreground), which is white in dark mode and only 2.07:1 on the
              accent; this is 8.08:1 in both themes. Hidden when unset (older recipes). */}
          {recipe.mealType ? (
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="px-3 py-1 rounded-full text-xs shadow-sm bg-[var(--accent)] text-[#2A1A16]">
                <span className="sr-only">Meal type: </span>
                {recipe.mealType.charAt(0).toUpperCase() + recipe.mealType.slice(1)}
              </span>
            </div>
          ) : null}

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {recipe.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs shadow-sm tag-mint"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Nutritional Information - Collapsible */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
            <button
              onClick={() => setIsNutritionExpanded(!isNutritionExpanded)}
              className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium">Nutritional Information</span>
              {isNutritionExpanded ? (
                <ChevronUp className="w-5 h-5 text-[#757575]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#757575]" />
              )}
            </button>
            
            {isNutritionExpanded && (
              <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                <p className="text-sm text-[#757575] mb-3">Per serving</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-[#757575]">Calories</p>
                    <p className="text-lg">{recipe.nutrition.calories} kcal</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#757575]">Protein</p>
                    <p className="text-lg">{recipe.nutrition.protein}g</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#757575]">Carbs</p>
                    <p className="text-lg">{recipe.nutrition.carbs}g</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#757575]">Fat</p>
                    <p className="text-lg">{recipe.nutrition.fat}g</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Ingredients */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl">Ingredients</h3>
              <button
                onClick={handleConvert}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors text-sm btn-pill-primary"
                aria-label="Convert units"
              >
                <ArrowLeftRight className="w-4 h-4" />
                <span>Convert</span>
              </button>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <ul className="space-y-3">
                {scaledIngredients.map((ingredient, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div
                      className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                      style={{ backgroundColor: 'var(--accent)' }}
                    />
                    <div className="flex-1">
                      <span className="text-gray-900">{ingredient.quantity}</span>
                      <span className="text-[#757575]"> {ingredient.name}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Method */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl">Method</h3>
                <button
                  onClick={handleStartStepByStep}
                  className="flex items-center gap-2 px-4 py-2 rounded-full transition-colors text-sm btn-pill-primary"
                  aria-label="Cook step by step"
                >
                  <PlayCircle className="w-4 h-4" />
                  <span>Cook step by step</span>
                </button>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <ol className="space-y-4">
                {displayedMethod.map((step, index) => (
                  <li key={index} className="flex gap-4">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-sm bg-accent text-accent-foreground">
                      {index + 1}
                    </div>
                    <p className="flex-1 text-gray-900 pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Source */}
          {recipe.source ? (
            <div className="mb-6 text-sm text-[#757575] flex items-start gap-2">
              <span className="font-medium text-gray-700">Source:</span>
              {/^https?:\/\//i.test(recipe.source) ? (
                <a
                  href={recipe.source}
                  target="_blank"
                  rel="noreferrer"
                  className="underline break-all link-ingredient"
                >
                  {recipe.source}
                </a>
              ) : (
                <span className="text-gray-800 break-words">{recipe.source}</span>
              )}
            </div>
          ) : null}

          {/* Add to Meal Plan Button */}
          {showAddToMealPlan && (
            <button
              onClick={() => onAddToMealPlan?.(recipe)}
              className="w-full py-3 rounded-full shadow-md transition-colors btn-primary-brand"
            >
              Add to Meal Plan
            </button>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav activeTab={activeTab} onTabChange={onTabChange} />

      {/* Unit Conversion Modal */}
      <UnitConversionModal
        isOpen={isUnitConversionModalOpen}
        onClose={() => setIsUnitConversionModalOpen(false)}
        currentUnit={currentUnitSystem}
        onUnitChange={handleUnitChange}
      />

      {/* Actions Menu Drawer */}
      {isActionsMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setIsActionsMenuOpen(false)}
          />
          
          {/* Drawer */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto">
            <div className="px-6 py-6">
              {/* Handle bar */}
              <div className="flex justify-center mb-4">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>

              <h3 className="text-lg mb-4">Recipe Actions</h3>

              <div className="space-y-3">
                {onEdit && (
                  <button
                    onClick={() => {
                      setIsActionsMenuOpen(false);
                      onEdit(recipe);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#E5F4EF] flex items-center justify-center">
                      <Edit2 className="w-5 h-5 icon-primary-strong" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900">Edit Recipe</p>
                      <p className="text-sm text-[#757575]">Update ingredients and instructions</p>
                    </div>
                  </button>
                )}

                {onDelete && (
                  <button
                    onClick={() => {
                      setIsActionsMenuOpen(false);
                      onDelete(recipe.id);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-4 bg-gray-50 rounded-2xl hover:bg-[#F6E7EE] transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#F6E7EE] flex items-center justify-center">
                      <Trash2 className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium" style={{ color: 'var(--color-accent)' }}>Delete Recipe</p>
                      <p className="text-sm text-[#757575]">Remove from your collection</p>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
    
  );
}
